<template>
  <ion-modal
    class="admin-releases-modal key-backup-modal"
    :is-open="isOpen"
    :backdrop-dismiss="mode === 'create' || mode === 'change'"
    @didDismiss="onDismiss"
  >
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ title }}</ion-title>
        <ion-buttons slot="end">
          <ion-button v-if="mode !== 'restore'" @click="close">Закрыть</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding key-backup-content">
      <!-- CREATE / CHANGE: показ кода -->
      <template v-if="(mode === 'create' || mode === 'change') && step === 'show'">
        <p class="key-backup-lead">
          Код нужен, чтобы прочитать историю Liga.Chat на новом телефоне или в другом браузере. Это не пароль входа в
          Liga.Team. Сервер видит только зашифрованный бекап — не сообщения и не код.
        </p>
        <div class="key-backup-warning">
          <strong>Запомните или запишите этот код.</strong>
          Если потеряете — историю Liga.Chat на новом устройстве <strong>не восстановить</strong>. Мы не сможем его
          сбросить: код есть только у вас.
        </div>
        <div v-if="mode === 'change'" class="key-backup-field">
          <label class="key-backup-label">Текущий код</label>
          <input
            v-model="oldCode"
            class="key-backup-input"
            type="text"
            inputmode="numeric"
            pattern="[0-9]{6}"
            maxlength="6"
            autocomplete="one-time-code"
            placeholder="••••••"
          />
        </div>
        <div class="key-backup-code-display" aria-label="Код восстановления">{{ generatedCode }}</div>
        <label class="key-backup-check">
          <input v-model="wroteDown" type="checkbox" />
          <span>Я записал(а) код в надёжном месте</span>
        </label>
        <ion-button
          class="messenger-primary-btn"
          expand="block"
          :disabled="!wroteDown || (mode === 'change' && !isValidCode(oldCode))"
          @click="step = 'confirm'"
        >
          Продолжить
        </ion-button>
        <ion-button v-if="mode === 'create'" class="messenger-clear-btn" expand="block" fill="clear" @click="later">
          Настроить позже
        </ion-button>
      </template>

      <!-- CREATE / CHANGE: подтверждение -->
      <template v-else-if="(mode === 'create' || mode === 'change') && step === 'confirm'">
        <div class="key-backup-warning">
          Введите код ещё раз, чтобы подтвердить, что вы его записали. Без кода историю на новом устройстве восстановить
          нельзя.
        </div>
        <div class="key-backup-field">
          <label class="key-backup-label">Повтор кода</label>
          <input
            v-model="confirmCode"
            class="key-backup-input"
            type="text"
            inputmode="numeric"
            pattern="[0-9]{6}"
            maxlength="6"
            autocomplete="one-time-code"
            placeholder="••••••"
          />
        </div>
        <p v-if="error" class="key-backup-error">{{ error }}</p>
        <ion-button class="messenger-primary-btn" expand="block" :disabled="busy || !isValidCode(confirmCode)" @click="submitCreate">
          {{ busy ? 'Сохранение…' : 'Сохранить бекап' }}
        </ion-button>
        <ion-button class="messenger-clear-btn" expand="block" fill="clear" :disabled="busy" @click="step = 'show'">
          Назад
        </ion-button>
      </template>

      <!-- RESTORE -->
      <template v-else-if="mode === 'restore'">
        <p class="key-backup-lead">
          Введите код восстановления Liga.Chat из 6 цифр, который вы записали. Без него история на этом устройстве
          останется недоступной.
        </p>
        <div class="key-backup-warning">
          Неверный код не удалит бекап — можно попробовать снова. Если пропустите восстановление, будет создано новое
          устройство без старых ключей.
        </div>
        <div class="key-backup-field">
          <label class="key-backup-label">Код восстановления</label>
          <input
            v-model="restoreCode"
            class="key-backup-input"
            type="text"
            inputmode="numeric"
            pattern="[0-9]{6}"
            maxlength="6"
            autocomplete="one-time-code"
            placeholder="••••••"
          />
        </div>
        <p v-if="error" class="key-backup-error">{{ error }}</p>
        <ion-button class="messenger-primary-btn" expand="block" :disabled="busy || !isValidCode(restoreCode)" @click="submitRestore">
          {{ busy ? 'Восстановление…' : 'Восстановить' }}
        </ion-button>
        <ion-button class="messenger-clear-btn" expand="block" fill="clear" :disabled="busy" @click="skipRestore">
          Пропустить
        </ion-button>
      </template>
    </ion-content>
  </ion-modal>
</template>

<script lang="ts">
import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonTitle, IonToolbar, toastController } from '@ionic/vue';
import { defineComponent, PropType } from 'vue';
import {
  changeKeyBackupCode,
  createKeyBackup,
  generateRecoveryCode,
  isValidRecoveryCode,
  markKeyBackupPromptSeen,
  restoreKeyBackup,
} from '@/services/messenger-crypto.service';
import type { MessengerKeyBackupBlob } from '@/services/messenger-crypto.service';

export type KeyBackupModalMode = 'create' | 'change' | 'restore';

export default defineComponent({
  name: 'MessengerKeyBackupModal',
  components: { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonTitle, IonToolbar },
  emits: ['done', 'skipped', 'created', 'restored'],
  props: {
    isOpen: { type: Boolean, required: true },
    mode: { type: String as PropType<KeyBackupModalMode>, required: true },
    backupBlob: { type: Object as PropType<MessengerKeyBackupBlob | null>, default: null },
  },
  data() {
    return {
      step: 'show' as 'show' | 'confirm',
      generatedCode: '',
      wroteDown: false,
      oldCode: '',
      confirmCode: '',
      restoreCode: '',
      error: '',
      busy: false,
      dismissedInternally: false,
    };
  },
  computed: {
    title(): string {
      if (this.mode === 'restore') return 'Восстановление Liga.Chat';
      if (this.mode === 'change') return 'Сменить код восстановления';
      return 'Код восстановления Liga.Chat';
    },
  },
  watch: {
    isOpen(value: boolean) {
      if (value) this.resetForm();
    },
    mode() {
      if (this.isOpen) this.resetForm();
    },
  },
  methods: {
    isValidCode(code: string): boolean {
      return isValidRecoveryCode(code);
    },
    resetForm() {
      this.step = 'show';
      this.generatedCode = generateRecoveryCode();
      this.wroteDown = false;
      this.oldCode = '';
      this.confirmCode = '';
      this.restoreCode = '';
      this.error = '';
      this.busy = false;
      this.dismissedInternally = false;
    },
    close() {
      this.dismissedInternally = true;
      this.$emit('done');
    },
    async later() {
      await markKeyBackupPromptSeen();
      this.dismissedInternally = true;
      this.$emit('skipped');
      this.$emit('done');
    },
    onDismiss() {
      if (this.dismissedInternally) return;
      if (this.mode === 'create') {
        void markKeyBackupPromptSeen();
        this.$emit('skipped');
      }
      this.$emit('done');
    },
    async submitCreate() {
      this.error = '';
      if (this.confirmCode.trim() !== this.generatedCode) {
        this.error = 'Код не совпадает. Проверьте запись и введите снова.';
        return;
      }
      this.busy = true;
      try {
        if (this.mode === 'change') {
          await changeKeyBackupCode(this.oldCode, this.generatedCode);
        } else {
          await createKeyBackup(this.generatedCode);
        }
        const toast = await toastController.create({
          message: 'Бекап ключей сохранён',
          duration: 2000,
          position: 'bottom',
        });
        await toast.present();
        this.dismissedInternally = true;
        this.$emit('created');
        this.$emit('done');
      } catch (e: unknown) {
        this.error = e instanceof Error ? e.message : 'Не удалось сохранить бекап';
      } finally {
        this.busy = false;
      }
    },
    async submitRestore() {
      this.error = '';
      this.busy = true;
      try {
        await restoreKeyBackup(this.restoreCode, this.backupBlob || undefined);
        const toast = await toastController.create({
          message: 'Ключи восстановлены',
          duration: 2000,
          position: 'bottom',
        });
        await toast.present();
        this.dismissedInternally = true;
        this.$emit('restored');
        this.$emit('done');
      } catch (e: unknown) {
        this.error = e instanceof Error ? e.message : 'Неверный код восстановления';
      } finally {
        this.busy = false;
      }
    },
    skipRestore() {
      this.dismissedInternally = true;
      this.$emit('skipped');
      this.$emit('done');
    },
  },
});
</script>

<style scoped>
.key-backup-content {
  --background: transparent;
}
.key-backup-lead {
  margin: 0 0 12px;
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.75));
  line-height: 1.45;
  font-size: 0.95rem;
}
.key-backup-warning {
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(235, 89, 82, 0.12);
  border: 1px solid rgba(235, 89, 82, 0.28);
  color: var(--liga-chrome-text, rgba(255, 255, 255, 0.88));
  font-size: 0.92rem;
  line-height: 1.45;
}
.key-backup-code-display {
  margin: 8px 0 18px;
  padding: 18px 12px;
  text-align: center;
  font-family: Montserrat, ui-monospace, monospace;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.35em;
  color: var(--liga-chrome-text);
  background: var(--liga-float-glass-bg-strong);
  border: 1px solid var(--liga-float-glass-border);
  border-radius: 14px;
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
}
.key-backup-field {
  margin-bottom: 14px;
}
.key-backup-label {
  display: block;
  margin-bottom: 6px;
  color: var(--liga-chrome-text-muted);
  font-size: 0.85rem;
  font-weight: 600;
}
.key-backup-input {
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  font-size: 16px;
  letter-spacing: 0.35em;
  text-align: center;
  font-family: Montserrat, ui-monospace, monospace;
  font-weight: 600;
  color: var(--liga-chrome-text);
  background: var(--liga-float-glass-bg-strong);
  border: 1px solid var(--liga-float-glass-border);
  border-radius: 14px;
  backdrop-filter: var(--liga-float-glass-blur);
  -webkit-backdrop-filter: var(--liga-float-glass-blur);
  box-shadow: var(--liga-float-glass-shadow);
  outline: none;
}
.key-backup-input::placeholder {
  color: var(--liga-chrome-text-muted);
  letter-spacing: 0.35em;
}
.key-backup-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  color: var(--liga-chrome-text);
  font-size: 0.95rem;
  line-height: 1.4;
}
.key-backup-check input {
  margin-top: 3px;
  flex-shrink: 0;
}
.key-backup-error {
  margin: 0 0 12px;
  color: var(--ion-color-danger, #eb5952);
  font-size: 0.9rem;
}
.messenger-primary-btn {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --color: var(--liga-chrome-text);
  --border-radius: 14px;
  font-weight: 600;
  margin-top: 8px;
}
.messenger-clear-btn {
  --color: var(--ion-color-orange);
  font-weight: 600;
}
</style>
